import {Entity} from "./Entity.js";
import {Field, field, type FieldValue} from "./Field.js";
import {FilterBuilder, type PathFilters} from "./Filter.js";
import {type Query, type QueryResult, toListQuery, totalCount} from "./Query.js";
import type {ListItem, RestClient} from "./RestClient.js";
import {
    type AnyField,
    type EntityRef,
    type FieldMap,
    isEntityRef,
    isJsonObject,
    type JsonObject,
    type JsonValue
} from "./types.js";

/** Fields every backend entity carries, injected so no model has to repeat them. */
export interface SystemFields {
    id: Field<number, false>;
    created: Field<Date>;
    modified: Field<Date>;
    deleted: Field<boolean>;
}

const systemFields = (): SystemFields => ({
    id: field.number().required().asReadOnly().named("id"),
    created: field.timestamp().asReadOnly().named("created"),
    modified: field.timestamp().asReadOnly().named("modified"),
    deleted: field.boolean().asReadOnly().named("deleted")
});

export type ModelFields<F extends FieldMap> = SystemFields & F;

export type EntityValues<F extends FieldMap> = { [K in keyof F]: FieldValue<F[K]> };

export interface ModelConfig<F extends FieldMap> {
    /** Name of the model, used wherever an entity has to name what it is. */
    readonly name: string;
    /** Path segment under the client's base path, for example `apps`. */
    readonly path: string;
    readonly fields: F;
}

/** What a view needs of an entity to list it: an identity and reads by path. */
export interface EntityRow {
    readonly id: number;

    read(path: string): unknown;
}

/**
 * The part of a model class that does not depend on its field map, so views and relations
 * can work with a model they only know structurally.
 */
export interface ModelStatics {
    readonly modelName: string;
    readonly path: string;
    readonly client: RestClient;
    readonly filter: PathFilters;

    /** Label of a field addressed by name, for columns and filters built from configuration. */
    labelFor(name: string): string;

    /** A field addressed by name, its precise value type erased, for views that only know a column list. */
    getFieldByName(name: string): AnyField | undefined;

    /** Parses a payload into the canonical instance for its id, creating it when unknown. */
    put(payload: JsonObject): void;

    /** Drops an id from the identity map, so the next read builds a fresh instance. */
    forget(id: number): void;

    /** The instance already loaded for an id, without a round trip. */
    cached<T extends AnyEntityClass>(this: T, id: number): InstanceType<T> | undefined;

    getList<T extends AnyEntityClass>(this: T, query?: Query): Promise<QueryResult<InstanceType<T>>>;

    getById<T extends AnyEntityClass>(this: T, id: number): Promise<InstanceType<T>>;

    /** The cached instance when it is already loaded, otherwise a fetch. */
    load<T extends AnyEntityClass>(this: T, id: number): Promise<InstanceType<T>>;
}

/** A model class seen without its field map, for relations and generic views. */
export interface AnyEntityClass extends ModelStatics {
    new(...args: never[]): EntityRow;
}

/**
 * A model: the class its entities are instances of, carrying the field map, the filter
 * builder and the reads and writes of that one backend resource.
 */
export interface EntityClass<F extends FieldMap> extends ModelStatics {
    readonly fields: ModelFields<F>;
    readonly filter: FilterBuilder<ModelFields<F>>;

    new(values: Partial<EntityValues<ModelFields<F>>>): Entity<F>;

    getField<K extends keyof ModelFields<F> & string>(name: K): ModelFields<F>[K];

    /**
     * Converts a payload into field values. Relations arrive either as a bare id or as a
     * nested object; both become an {@link EntityRef}, and a nested payload is also cached
     * by the model it belongs to, so there stays one instance per entity.
     */
    parse(payload: JsonObject): Partial<EntityValues<ModelFields<F>>>;

    /** Values to send back, skipping read-only and file fields, with relations reduced to ids. */
    serialize(values: Partial<EntityValues<ModelFields<F>>>): JsonObject;

    put<T extends EntityClass<F>>(this: T, payload: JsonObject): InstanceType<T>;

    create<T extends EntityClass<F>>(this: T, values: Partial<EntityValues<ModelFields<F>>>): Promise<InstanceType<T>>;
}

/**
 * Identity maps, one per model class. Keying on the class itself keeps the map with the model
 * that owns it without a registry every application would have to keep in sync.
 */
const identityMaps = new WeakMap<object, Map<number, EntityRow>>();

const instancesOf = (modelClass: object): Map<number, EntityRow> => {
    const existing = identityMaps.get(modelClass);
    if (existing !== undefined) {
        return existing;
    }
    const created = new Map<number, EntityRow>();
    identityMaps.set(modelClass, created);
    return created;
};

/**
 * Builds the `defineModel` of one application, bound to the transport its models read
 * and write through.
 */
export const createModelFactory = (client: RestClient) =>
    <F extends FieldMap>(config: ModelConfig<F>): EntityClass<F> => {
        const named = Object.fromEntries(Object.entries(config.fields)
            .map(([key, value]) => [key, value.name === undefined ? value.named(key) : value]));
        const fields = {...systemFields(), ...named} as ModelFields<F>;
        const labels = new Map(Object.entries(fields).map(([key, value]) => [key, (value as AnyField).label]));
        const resourcePath = `/${config.path}`;

        class ModelClass extends Entity<F> {
            static readonly modelName = config.name;
            static readonly path = config.path;
            static readonly client = client;
            static readonly fields = fields;
            static readonly filter = new FilterBuilder<ModelFields<F>>(new Set(Object.keys(fields)));

            static labelFor(name: string): string {
                return labels.get(name) ?? name;
            }

            static getFieldByName(name: string): AnyField | undefined {
                return (fields as Record<string, AnyField>)[name];
            }

            static getField<K extends keyof ModelFields<F> & string>(name: K): ModelFields<F>[K] {
                const found = fields[name];
                if (found === undefined) {
                    throw new Error(`Model ${config.name} has no field "${name}"`);
                }
                return found;
            }

            static parse(payload: JsonObject): Partial<EntityValues<ModelFields<F>>> {
                const values: Record<string, unknown> = {};
                for (const [key, modelField] of Object.entries(fields) as [string, AnyField][]) {
                    const rawValue = payload[key];
                    if (rawValue === undefined) {
                        continue;
                    }
                    values[key] = rawValue === null ? null : parseField(modelField, rawValue);
                }
                return values as Partial<EntityValues<ModelFields<F>>>;
            }

            static serialize(values: Partial<EntityValues<ModelFields<F>>>): JsonObject {
                const payload: JsonObject = {};
                for (const [key, value] of Object.entries(values)) {
                    const modelField = fields[key];
                    if (modelField === undefined || modelField.readOnly || modelField.kind === "file") {
                        continue;
                    }
                    payload[key] = serializeField(modelField, value);
                }
                return payload;
            }

            static put(payload: JsonObject): ModelClass {
                const values = this.parse(payload);
                const id = values.id;
                if (typeof id !== "number") {
                    return new this(values);
                }
                const instances = instancesOf(this);
                const existing = instances.get(id);
                if (existing instanceof ModelClass) {
                    existing.refresh(values);
                    return existing;
                }
                const entity = new this(values);
                instances.set(id, entity);
                return entity;
            }

            static cached(id: number): ModelClass | undefined {
                const found = instancesOf(this).get(id);
                return found instanceof ModelClass ? found : undefined;
            }

            static forget(id: number): void {
                instancesOf(this).delete(id);
            }

            static async getList(query: Query = {}): Promise<QueryResult<ModelClass>> {
                const listPath = query.subPath === undefined ? resourcePath : `${resourcePath}/${query.subPath}`;
                const envelope = await client.getList(listPath, toListQuery(query));
                // A list entry elided to a bare id is the entity itself, cached or fetched.
                const resolve = (item: ListItem): ModelClass | Promise<ModelClass> =>
                    typeof item === "number" ? this.load(item) : this.put(item);
                return {
                    entities: await Promise.all(envelope.data.map(resolve)),
                    totalCount: totalCount(envelope.metadata),
                    aggregations: envelope.metadata.aggregations ?? []
                };
            }

            static async getById(id: number): Promise<ModelClass> {
                return this.put(await client.getOne(`${resourcePath}/${id}`));
            }

            static async load(id: number): Promise<ModelClass> {
                return this.cached(id) ?? await this.getById(id);
            }

            static async create(values: Partial<EntityValues<ModelFields<F>>>): Promise<ModelClass> {
                const envelope = await client.post(resourcePath, this.serialize(values));
                return this.put(envelope.data);
            }
        }

        // The statics above are written against this one class; the interface restates them so
        // that a model extending it reads its own entities back.
        return ModelClass as unknown as EntityClass<F>;
    };

const parseField = (modelField: AnyField, rawValue: JsonValue): unknown => {
    if (modelField.kind === "relation") {
        return toRef(modelField, rawValue);
    }
    if (modelField.kind === "relationList") {
        return Array.isArray(rawValue)
            ? rawValue.map(item => toRef(modelField, item)).filter(ref => ref !== null)
            : [];
    }
    return parseScalar(modelField.kind, rawValue);
};

const parseScalar = (kind: string, raw: JsonValue): unknown => {
    switch (kind) {
        case "date":
        case "timestamp":
            return typeof raw === "string" || typeof raw === "number" ? new Date(raw) : null;
        case "number":
        case "duration":
            return typeof raw === "number" ? raw : Number(raw);
        case "boolean":
            return Boolean(raw);
        case "string":
        case "enum":
            return typeof raw === "string" ? raw : String(raw);
        default:
            return raw;
    }
};

const toRef = (modelField: AnyField, rawValue: JsonValue): EntityRef | null => {
    if (typeof rawValue === "number") {
        return {id: rawValue};
    }
    if (isJsonObject(rawValue) && typeof rawValue["id"] === "number") {
        modelField.config.relationTarget?.().put(rawValue);
        return {id: rawValue["id"]};
    }
    return null;
};

const serializeField = (modelField: AnyField, value: unknown): JsonValue => {
    if (value === null || value === undefined) {
        return null;
    }
    if (modelField.kind === "relation") {
        return isEntityRef(value) ? value.id : (value as JsonValue);
    }
    if (modelField.kind === "relationList" && Array.isArray(value)) {
        return value.map(item => (isEntityRef(item) ? item.id : (item as JsonValue)));
    }
    if (value instanceof Date) {
        return modelField.kind === "date" ? value.toISOString().slice(0, 10) : value.toISOString();
    }
    return value as JsonValue;
};
